import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { saveAs } from "file-saver";
import { Spinner } from "react-bootstrap";
import NEW_API from "../../../Api/NewApi";
import Button from "../../Common/FormElements/Button/Button";
import Select from "../../Common/FormElements/Select/Select";
import TabsNavbar from "../../Common/TabsNavBar/TabsNavbar";
import CustomLineChart from "../../Common/CustomLineChart/CustomLineChart";
import AnnualDataTable from "../../Revendeur/AnnualDataTable";
import DonutChart from "../../Dashboard/SoldProducts/Chart";
import { CATEGORY_META, formatEuro } from "./statisticsData";

// Category sub-tabs (raw i18n keys — TabsNavbar translates internally).
const PRODUCT_CATEGORIES = [
  "spa",
  "swimSpa",
  "sauna",
  "accessory",
  "sparePart",
];
const TAB_LABELS = PRODUCT_CATEGORIES.map(
  (key) => CATEGORY_META.find((c) => c.key === key).label,
);
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR - 2, CURRENT_YEAR - 1, CURRENT_YEAR];
const YEAR_OPTIONS = YEARS.map((y) => ({ name: String(y), code: y }));

// Comparison chart metrics (screenshot: CA Actuel / Nbre / Marge net / TX Marge).
const METRIC_TABS = ["Revenue", "Quantity", "Net margin", "Margin rate"];
const METRIC_KEYS = {
  Revenue: "ca",
  Quantity: "quantity",
  "Net margin": "marge",
  "Margin rate": "txMarge",
};
// Product 1 orange, product 2 green.
const COMPARE_COLORS = ["#F2A741", "#41BFA3"];

const csvCell = (value) => {
  const text = String(value ?? "");
  return /[;"\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/**
 * Page 2 — Produit, one section per category tab: top products (table), a
 * monthly comparison of 2 products of the category, the quantity pie (own
 * year select), PDF export and AI CSV report.
 * Reseller / business affiliate: the same page on their orders (server-scoped).
 */
const ProductStats = () => {
  const { t } = useTranslation("common");
  const { scope } = useSelector((state) => state.appCustomersReducer);
  // Espace clients filter bar (admin: store / affiliate; affiliate: store).
  const scopeParams = {
    organizationId: scope.organizationId || undefined,
    businessAffiliateId: scope.businessAffiliateId || undefined,
  };
  const [activeTab, setActiveTab] = useState(TAB_LABELS[0]);
  const category = PRODUCT_CATEGORIES[TAB_LABELS.indexOf(activeTab)];
  const isSpaCategory = ["spa", "swimSpa", "sauna"].includes(category);

  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [pie, setPie] = useState({ labels: [], data: [] });
  const [year, setYear] = useState(CURRENT_YEAR);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reporting, setReporting] = useState(false);

  const [monthly, setMonthly] = useState(null);
  const [activeMetric, setActiveMetric] = useState(METRIC_TABS[0]);
  // Comparison: 2 products of the category, among those sold in `year`.
  const [productOptions, setProductOptions] = useState([]);
  const [compareIds, setCompareIds] = useState([null, null]);
  const [pieYear, setPieYear] = useState(CURRENT_YEAR);

  const abortRef = useRef(null);
  const fetchData = async (page, selectedYear, search) => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    setLoading(true);
    try {
      const { data } = await NEW_API.get("api/statistics/products", {
        params: { category, year: selectedYear, page, search, ...scopeParams },
        signal: abortRef.current.signal,
      });
      setRows((prev) => (page > 1 ? [...prev, ...data.data] : data.data));
      setTotalPages(data.meta?.pagination?.pageCount ?? 0);
      setYear(selectedYear);
      if (page === 1) {
        const options = (data.products ?? []).map((product) => ({
          name: product.name,
          code: product.id,
        }));
        setProductOptions(options);
        // Keep the picks still sold this year, fill the rest with the best sellers.
        setCompareIds((prev) => {
          const kept = prev.filter((id) => options.some((o) => o.code === id));
          const fill = options
            .map((option) => option.code)
            .filter((id) => !kept.includes(id));
          return [kept[0] ?? fill.shift() ?? null, kept[1] ?? fill.shift() ?? null];
        });
      }
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
        return;
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const [firstId, secondId] = compareIds;
  const monthlyAbortRef = useRef(null);
  useEffect(() => {
    monthlyAbortRef.current?.abort();
    const productIds = [firstId, secondId].filter(Boolean);
    if (!productIds.length) {
      setMonthly(null);
      return;
    }
    monthlyAbortRef.current = new AbortController();
    (async () => {
      try {
        const { data } = await NEW_API.get("api/statistics/monthly", {
          params: { year, productIds: productIds.join(","), ...scopeParams },
          signal: monthlyAbortRef.current.signal,
        });
        setMonthly(data?.data);
      } catch (error) {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
          return;
        console.log(error);
      }
    })();
    return () => monthlyAbortRef.current?.abort();
  }, [year, firstId, secondId]);

  // Pie: the category's split for its own year.
  const pieAbortRef = useRef(null);
  useEffect(() => {
    pieAbortRef.current?.abort();
    pieAbortRef.current = new AbortController();
    (async () => {
      try {
        const { data } = await NEW_API.get("api/statistics/products", {
          params: { category, year: pieYear, page: 1, ...scopeParams },
          signal: pieAbortRef.current.signal,
        });
        setPie(data?.pie ?? { labels: [], data: [] });
      } catch (error) {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
          return;
        console.log(error);
      }
    })();
    return () => pieAbortRef.current?.abort();
  }, [category, pieYear]);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const response = await NEW_API.post(
        "api/statistics/products-pdf",
        { data: { category, year } },
        { params: scopeParams, responseType: "blob" },
      );
      saveAs(
        new Blob([response.data], { type: "application/pdf" }),
        `produits_${category}_${year}.pdf`,
      );
    } catch (error) {
      console.log(error);
    } finally {
      setExporting(false);
    }
  };

  const exportReport = async () => {
    setReporting(true);
    try {
      const { data } = await NEW_API.get("api/statistics/products-report", {
        params: { category, ...scopeParams },
      });
      // Partners get no margin (the server strips it too).
      const header =
        "product;range;category;year;quantity;revenue_ht;net_margin;margin_rate;sav_count";
      const lines = (data?.data ?? []).map((row) =>
        [
          row.name,
          row.gamme,
          row.category,
          row.year,
          row.quantity,
          row.ca,
          row.margeNet,
          row.txMarge,
          row.savCount,
        ]
          .map(csvCell)
          .join(";"),
      );
      saveAs(
        new Blob(["﻿" + [header, ...lines].join("\n")], {
          type: "text/csv;charset=utf-8",
        }),
        `products_report_${category}_${moment().format("YYYY-MM-DD")}.csv`,
      );
    } catch (error) {
      console.log(error);
    } finally {
      setReporting(false);
    }
  };

  const headers = [
    "Product",
    ...(isSpaCategory ? ["Range"] : []),
    "Quantity",
    "Revenue",
    "Net margin",
    "Margin rate",
    "SAV",
  ];
  const columns = [
    { name: "Product", render: (row) => row.name },
    ...(isSpaCategory
      ? [{ name: "Range", render: (row) => row.gamme ?? "-" }]
      : []),
    { name: "Quantity", render: (row) => row.quantity },
    { name: "Revenue", render: (row) => formatEuro(row.ca) },
    { name: "Net margin", render: (row) => formatEuro(row.margeNet) },
    { name: "Margin rate", render: (row) => `${row.txMarge ?? 0}%` },
    { name: "SAV", render: (row) => row.savCount ?? 0 },
  ];

  // Product 1 vs product 2, month by month over the table's year.
  const metricKey = METRIC_KEYS[activeMetric];
  const isRate = metricKey === "txMarge";
  const isCount = metricKey === "quantity";
  const compareSeries = compareIds
    .map((id, index) => ({
      id,
      color: COMPARE_COLORS[index],
      name: productOptions.find((option) => option.code === id)?.name ?? `#${id}`,
    }))
    .filter((series) => series.id);
  const compareData = Object.fromEntries(
    compareSeries.map(({ id, name }) => [
      name,
      monthly?.[metricKey]?.[id]?.current ?? Array(12).fill(0),
    ]),
  );
  const pickProduct = (slot, id) =>
    setCompareIds((prev) => prev.map((value, index) => (index === slot ? id : value)));

  return (
    <div className="position-relative mt-3">
      {loading && (
        <div className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-50 z-9999 d-flex align-items-center justify-content-center">
          <Spinner />
        </div>
      )}
      <div className="d-flex align-items-end flex-wrap gap-3 mb-2">
        <div className="me-auto">
          <TabsNavbar
            tabs={TAB_LABELS}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        </div>
        <Button
          variant="outline"
          loading={reporting}
          Icon={() => <i className="bi bi-filetype-csv fa-lg" />}
          onClick={exportReport}
        >
          {t("AI report")}
        </Button>
        <Button
          variant="outline"
          loading={exporting}
          Icon={() => <i className="bi bi-download fa-lg" />}
          onClick={exportPdf}
        >
          {t("Export PDF")}
        </Button>
      </div>

      <AnnualDataTable
        title="Products"
        headers={headers}
        columns={columns}
        data={rows}
        totalPages={totalPages}
        fetchData={fetchData}
        years={YEARS}
        noResultsMessage="No results found"
        filters={category}
      />

      <div className="row g-4 mt-2">
        <div className="col-12 col-lg-8">
          <h4 className="f16 sBold text-ultraBlack">
            {t("Comparison by product")}
          </h4>
          <TabsNavbar
            tabs={METRIC_TABS}
            activeTab={activeMetric}
            setActiveTab={setActiveMetric}
          />
          <div className="d-flex flex-wrap gap-3 mt-2">
            {compareIds.map((id, slot) => (
              <div key={slot} className="max-w-300px w-100">
                <Select
                  name={`product${slot + 1}`}
                  // A product can't be compared with itself.
                  options={productOptions.filter(
                    (option) => option.code !== compareIds[1 - slot],
                  )}
                  value={productOptions.find((option) => option.code === id) ?? null}
                  placeholder={t(slot === 0 ? "Product 1" : "Product 2")}
                  filter
                  resetable
                  callback={(e) => pickProduct(slot, e.value?.code ?? null)}
                />
              </div>
            ))}
          </div>
          <div className="d-flex flex-wrap gap-3 mt-2">
            {compareSeries.map(({ id, name, color }) => (
              <span
                key={id}
                className="d-flex align-items-center gap-1 f13 sBold text-lightBlack"
              >
                <span
                  className="d-inline-block br50"
                  style={{ width: 10, height: 10, backgroundColor: color }}
                />
                {`${name} ${year}`}
              </span>
            ))}
          </div>
          <div className="mt-2">
            <CustomLineChart
              years={Object.keys(compareData)}
              colors={compareSeries.map((series) => series.color)}
              data={compareData}
              unity={isRate ? "%" : metricKey === "quantity" ? "" : "k"}
              hasUnity={!isCount}
            />
          </div>
        </div>
        <div className="col-12 col-lg-4">
          <div className="max-w-200px w-100 mb-2">
            <Select
              name="pieYear"
              options={YEAR_OPTIONS}
              value={YEAR_OPTIONS.find((option) => option.code === pieYear)}
              resetable
              callback={(e) => e.value?.code && setPieYear(e.value.code)}
            />
          </div>
          <h4 className="f16 sBold text-ultraBlack">
            {t("Percentage per product")}
          </h4>
          <DonutChart
            data={pie.data.slice(0, 10)}
            labels={pie.labels.slice(0, 10)}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductStats;
